import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Link, useNavigate } from "react-router-dom";
import { getAuth, signInWithEmailAndPassword, signOut, signInWithCustomToken, sendPasswordResetEmail } from 'firebase/auth';

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import bgImage from "../../../assets/images/FX-Bullsheet-bg.png";

const Basic = ({ setUserID }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loginMessage, setLoginMessage] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(4);
  const auth = getAuth();

  useEffect(() => {
    const storedUserID = localStorage.getItem('userID');
    if (storedUserID) {
      setIsLoggedIn(true);
      setUserID(storedUserID);
    }
  }, [setUserID]);

// Add countdown effect
useEffect(() => {
  let timer;
  if (isLoggedIn) {
    timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          navigate('/fx-dashboard');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }
  return () => clearInterval(timer);
}, [isLoggedIn, navigate]);

  const login = async () => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      setUserID(result.user.uid);
      if (rememberMe) {
        localStorage.setItem('email', email);
        localStorage.setItem('password', password);
      }
      localStorage.setItem('userID', result.user.uid);
      setLoginMessage('Successfully logged in');
      setIsLoggedIn(true);
      setTimeout(() => {
        navigate('/home-dashboard');
      }, 2500);
    } catch (error) {
      setLoginMessage('Failed to log in');
    }
  };

  const loginWithWordPress = async () => {
    try {
      const response = await fetch('https://fxbullsheet.com/wp-json/firebase/v1/custom-token', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (data.customToken) {
        await loginWithCustomToken(data.customToken);
      } else {
        setLoginMessage('Failed to get custom token');
      }
    } catch (error) {
      setLoginMessage('Failed to log in with WordPress');
    }
  };

  const loginWithCustomToken = async (customToken) => {
    try {
      await signInWithCustomToken(auth, customToken);
      const userId = auth.currentUser.uid;
      if (userId) {
        setUserID(userId);
        setLoginMessage('Successfully logged in');
        setTimeout(() => {
          navigate('/home-dashboard');
        }, 2500);
      } else {
        setLoginMessage('Failed to retrieve user ID after login');
      }
    } catch (error) {
      setLoginMessage('Failed to log in with custom token');
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('userID');
      setIsLoggedIn(false);
      setLoginMessage('');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const handlePasswordReset = async () => {
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('Password reset email sent successfully.');
    } catch (error) {
      setResetMessage('Failed to send password reset email.');
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {isLoggedIn ? "You're already logged in!" : "Sign in"}
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          {isLoggedIn ? (
            <MDBox textAlign="center">
              <MDTypography variant="h6" color="success">
              You're already logged in! Redirecting to dashboard in {countdown} seconds...
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox component="form" role="form" onSubmit={(e) => { e.preventDefault(); login(); }}>
              <MDBox mb={2}>
                <MDInput type="email" label="Email" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
              </MDBox>
              <MDBox mb={2}>
                <MDInput type="password" label="Password" fullWidth value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
              </MDBox>
              <MDBox display="flex" alignItems="center" ml={-1}>
                <Switch checked={rememberMe} onChange={handleSetRememberMe} />
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  onClick={handleSetRememberMe}
                  sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
                >
                  &nbsp;&nbsp;Remember me
                </MDTypography>
              </MDBox>
              <MDBox mt={4} mb={1}>
                <MDButton variant="gradient" color="info" fullWidth type="submit">
                  sign in
                </MDButton>
              </MDBox>
              <MDBox mt={3} mb={1} textAlign="center">
  <MDTypography variant="button" color="text">
    Don't have an account?{" "}
    <MDTypography
      component={Link}
      to="/authentication/sign-up"
      variant="button"
      color="info"
      fontWeight="medium"
      textGradient
    >
      Sign up
    </MDTypography>
  </MDTypography>
</MDBox>
            </MDBox>
            
          )}
          {loginMessage && (
            <MDBox mt={2} mb={1} textAlign="center">
              <MDTypography variant="button" color="success" sx={{ fontSize: '1rem' }}>
                {loginMessage}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </Card>
      {isLoggedIn && (
        <MDBox mt={2} mb={1} textAlign="center">
          <MDButton variant="gradient" color="error" fullWidth onClick={logout}>
            Logout
          </MDButton>
        </MDBox>
      )}
<MDBox mt={3} mb={1} textAlign="center">

</MDBox>
      {!isLoggedIn && (
        <Card>
          <MDBox pt={4} pb={3} px={3}>
            <MDTypography variant="h6" textAlign="center" mb={2}>
              Reset Password
            </MDTypography>
            <MDBox component="form" role="form" onSubmit={(e) => { e.preventDefault(); handlePasswordReset(); }}>
              <MDBox mb={2}>
                <MDInput type="email" label="Email" fullWidth value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} autoComplete="email" />
              </MDBox>
              <MDBox mt={4} mb={1}>
                <MDButton variant="gradient" color="info" fullWidth type="submit">
                  Send Password Reset Email
                </MDButton>
              </MDBox>
            </MDBox>
            {resetMessage && (
              <MDBox mt={2} mb={1} textAlign="center">
                <MDTypography variant="button" color="success" sx={{ fontSize: '1rem' }}>
                  {resetMessage}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </Card>
      )}
    </BasicLayout>
  );
};

Basic.propTypes = {
  setUserID: PropTypes.func.isRequired,
};

export default Basic;
